<template>
    <div class="survey-item md:tw-flex tw-space-x-8 tw-items-start tw-justify-center tw-group">
        <div class="tw-flex-1 tw-space-y-4">
            <div class="group-hover:tw-text-indigo-500 tw-cursor-pointer truncate-2 tw--mb-2">
                {{ surveyName }}
            </div>
            <div v-if="!isDynamicSurvey && survey.status == 'active'">
                <div class="tw-max-w-xs">
                    <div class="tw-h-2 tw-bg-gray-100 tw-rounded-sm tw-overflow-hidden">
                        <div
                            class="tw-h-2 tw-bg-indigo-500"
                            :style="{
                                width: `${
                                    !survey.activePeriodDetails
                                        ? 0
                                        : survey.activePeriodDetails.progressInPercent
                                }%`,
                            }"
                        ></div>
                    </div>
                </div>
                <div class="tw-text-sm tw-text-gray-400 tw-mt-1">
                    <template v-if="!survey.activePeriodDetails">
                        {{ $t("surveys.list.answerPeriodClosed") }}
                    </template>
                    <template v-else>
                        {{ $t("surveys.list.answerPeriodEnds") }}
                        {{ survey.activePeriodDetails.endDate | moment("from", "now") }}
                    </template>
                </div>
            </div>
            <div class="tw-text-sm">
                {{ $t("surveys.list.labelOwnedBy") }}
                {{ ownerPreviewText }}
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-space-x-4 tw-text-gray-500">
                <span
                    class="tw-inline-block tw-rounded tw-py-0 tw-px-2 tw-border tw-border-solid tw-border-gray-200"
                    >{{ statusText }}</span
                >

                <span v-if="survey.settings.notAnonymous">
                    <i class="fa fa-eye tw-mr-2"></i>
                    <span>{{ $t("surveys.list.labelNotAnonymous") }}</span>
                </span>
                <span v-else-if="!survey.settings.notAnonymous">
                    <i class="fa fa-eye-slash tw-mr-2"></i>
                    <span>{{ $t("surveys.list.labelAnonymous") }}</span>
                </span>

                <span v-if="survey.timing.frequency">
                    <i class="fa fa-repeat tw-mr-2"></i>
                    <span v-if="survey.timing.frequency == 'every week'">{{
                        $t("surveys.details.optionWeekly")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'every 2 weeks'">{{
                        $t("surveys.details.optionBiweekly")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'every month'">{{
                        $t("surveys.details.optionMonthly")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'every 2 months'">{{
                        $t("surveys.details.option2Months")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'every 3 months'">{{
                        $t("surveys.details.option3Months")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'every 4 months'">{{
                        $t("surveys.details.option4Months")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'every 6 months'">{{
                        $t("surveys.details.option6Months")
                    }}</span>
                    <span v-else-if="survey.timing.frequency == 'once' || isDynamicSurvey">{{
                        $t("surveys.details.optionOnce")
                    }}</span>
                </span>
            </div>
        </div>
        <div
            v-if="survey.status == 'active' && survey.timing.nextPeriodStart"
            class="tw-flex-shrink-0 tw-text-right"
        >
            <div class="tw-text-base tw-text-indigo-500 tw-font-bold">
                <strong v-if="isDynamicSurvey">{{ $t("surveys.details.dynamicTrigger") }} </strong>
                <strong v-else>
                    {{ survey.timing.nextPeriodStart | moment("MMM D, YYYY") }}
                </strong>
            </div>
            <div class="tw-text-gray-400">
                <span v-show="!isDynamicSurvey">{{ $t("surveys.list.labelNextRound") }}</span>
                <span v-show="survey.periods && !isDynamicSurvey">
                    - {{ survey.periods }} {{ $t("surveys.details.soFar") }}
                </span>
                <span v-show="['ONCE_AFTER_START'].includes(survey.timing.frequency)"
                    >{{ $t("surveys.details.typeOnBoarding") }}
                </span>
                <span v-show="['ONCE_BEFORE_END'].includes(survey.timing.frequency)"
                    >{{ $t("surveys.details.typeOffBoarding") }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        survey: {
            type: Object,
            required: true,
        },
    },
    computed: {
        surveyName() {
            return this.survey?.name || "";
        },
        ownerPreviewText() {
            const maxAmount = 5;

            if (!Array.isArray(this.survey.owners)) return "";
            const ownerNames = this.survey.owners.map(owner => owner.fullname);

            if (ownerNames.length <= maxAmount) return ownerNames.join(", ");

            return `${ownerNames.slice(0, maxAmount).join(", ")}...`;
        },
        statusText() {
            switch (this.survey.status) {
                case "draft":
                    return this.$t("surveys.list.labelDraft");
                case "paused":
                    return this.$t("surveys.list.labelPaused");
                case "active":
                    return this.$t("surveys.list.labelActive");
                case "closed":
                    return this.$t("surveys.list.labelClosed");
                default:
                    return "N/A";
            }
        },
        isDynamicSurvey() {
            return ["ONCE_AFTER_START", "ONCE_BEFORE_END"].includes(this.survey.timing.frequency);
        },
    },
};
</script>

<style scoped>
.truncate-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
